import {
  Component,
  OnInit,
  HostBinding,
  AfterContentInit,
  ContentChildren,
  QueryList,
  Renderer2,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';

import { PgSuffixIconDirective } from './pg-suffix-icon.directive';
import { Subscription, combineLatest } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import { styleVariables } from '../../styles/variables';
import { AnimationService } from '../../core/ui/animation.service';

export interface SuffixIconState {
  isVisible: boolean;
  order: number;
  xPosition: number;
  elementRef: ElementRef;
}

/*
  Unlike the subscript messages component
  This component is not dependent on the internal state
  of the parent field and control.

  So instead the user must trigger state changes via the input
  bindings on the child suffix icons.
*/
@Component({
  selector: 'pg-suffix-icons, grit-suffix-icons',
  templateUrl: './pg-suffix-icons.component.html',
  styleUrls: ['./pg-suffix-icons.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PgSuffixIconsComponent
  implements OnInit, AfterContentInit, OnDestroy {
  @HostBinding('class')
  _classList = 'pg-suffix-icons grit-suffix-icons';

  @HostBinding('style.opacity')
  _styleOpacity = '0';

  @ContentChildren(PgSuffixIconDirective)
  _iconChildren: QueryList<PgSuffixIconDirective>;

  private _previousSuffixState: SuffixIconState[];
  private _globalPromise = Promise.resolve();

  readonly iconBaseOffset = 0;
  readonly iconIntervalOffset = 32;

  _iconSubscription: Subscription;
  _queryListSubscription: Subscription;

  get hostEl(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  constructor(
    private _animation: AnimationService,
    private _renderer: Renderer2,
    public elementRef: ElementRef
  ) {}

  ngOnInit() {}

  ngAfterContentInit() {
    this._registerIconSubscriptions();
    this._queryListSubscription = this._iconChildren.changes.subscribe(() => {
      this._registerIconSubscriptions();
    });
  }

  ngOnDestroy() {
    if (this._queryListSubscription) {
      this._queryListSubscription.unsubscribe();
    }
    if (this._iconSubscription) {
      this._iconSubscription.unsubscribe();
    }
  }

  private _registerIconSubscriptions() {
    const eventCycleDebounceTime = 150;

    if (this._iconSubscription && !this._iconSubscription.closed) {
      this._iconSubscription.unsubscribe();
    }

    const source = combineLatest(
      ...this._iconChildren.map(ic => ic.visibleChanged.pipe(startWith(null)))
    ).pipe(debounceTime(eventCycleDebounceTime));

    this._iconSubscription = source.subscribe(() => {
      this._reconcileSuffixState(false).then(() => {
        this._styleOpacity = '1';
      });
    });
  }

  // ---=== ---=== ---===

  private _reconcileSuffixState(ignorePrevious = false) {
    let finalPromise: any = Promise.resolve();

    // First call, we have no idea what the suffix state is.
    // So we will calculate it and keep it stored.
    if (!this._previousSuffixState || ignorePrevious) {
      this._previousSuffixState = this._calculateCurrentSuffixState();
      this._previousSuffixState.forEach(state => {
        this._setStateStyles(state);
      });
      return Promise.resolve();
    } else {
      const newSuffixState: SuffixIconState[] = this._calculateCurrentSuffixState();

      if (newSuffixState.length !== this._previousSuffixState.length) {
        // NOTE: we can probably gracefully handle this, but for now, we are just going to
        // ignore the animations and update state. In general, these two states should
        // not be different lengths for supported behavior
        newSuffixState.forEach(state => {
          this._setStateStyles(state);
        });
      } else {
        const leavingAnimations: Function[] = [];
        const shiftingAnimations: Function[] = [];
        const enteringAnimations: Function[] = [];

        for (let i = 0; i < newSuffixState.length; i++) {
          const oldState: SuffixIconState = this._previousSuffixState[i];
          const newState: SuffixIconState = newSuffixState[i];
          const newX = newState.xPosition;
          const oldX = oldState.xPosition;

          /*
            This is going to happen for 3 state changes
            1.) are icons disappearing - these animations happen first
            2.) are icons shifitng position - these animations happen second
            3.) are icons appearing - these animations happen third
          */

          // An icon is disappearing because it was previously visible
          // and now it is not.
          if (oldState.isVisible && !newState.isVisible) {
            this._setPreHiddenStyle(oldState.elementRef.nativeElement);

            leavingAnimations.push(() =>
              this._animation
                .animate({
                  targets: newState.elementRef,
                  opacity: 0,
                  transform: `translateX(-${oldX}px) scale(.5)`,
                  duration: 150,
                  easing: styleVariables.transitionCurveSharp,
                })
                .then(() =>
                  this._setPostHiddenStyle(newState.elementRef.nativeElement)
                )
            );
            // An icon is already visible and it's xPosition is changing
          } else if (
            oldState.xPosition !== newState.xPosition &&
            newState.isVisible &&
            oldState.isVisible
          ) {
            shiftingAnimations.push(() =>
              this._animation
                .animate({
                  targets: newState.elementRef,
                  transform: `translateX(-${newX}px) scale(1)`,
                  duration: 150,
                  easing: styleVariables.transitionCurveSharp,
                })
                .then(() =>
                  this._setPostShiftStyle(
                    newState.elementRef.nativeElement,
                    newX
                  )
                )
            );
            // An icon is appearing
          } else if (!oldState.isVisible && newState.isVisible) {
            this._setPreVisibleStyle(newState.elementRef.nativeElement, newX);

            enteringAnimations.push(() => {
              this._animation
                .animate({
                  targets: newState.elementRef,
                  opacity: [0, 1],
                  duration: 150,
                  transform: `translateX(-${newX}px) scale(1)`,
                })
                .then(() => {
                  this._setPostVisibleStyle(
                    newState.elementRef.nativeElement,
                    newX
                  );
                });
            });
          }
        }

        const targetSuffixWrapperWidth = newSuffixState.reduce(
          (accumulator, cur, i) => {
            if (cur.isVisible) {
              return accumulator + this.iconIntervalOffset;
            }
            return accumulator;
          },
          this.iconBaseOffset
        );
        shiftingAnimations.push(() =>
          this._animation.animate({
            targets: this.elementRef,
            width: targetSuffixWrapperWidth,
            fillMode: 'forwards',
            duration: 150,
          })
        );

        finalPromise = this._globalPromise
          .then(() => Promise.all(leavingAnimations.map(a => a())))
          .then(() => Promise.all(shiftingAnimations.map(b => b())))
          .then(() => Promise.all(enteringAnimations.map(c => c())));
      }

      this._previousSuffixState = newSuffixState;
      this._globalPromise = finalPromise;
      return finalPromise;
    }
  }

  private _setStateStyles(iconState: SuffixIconState) {
    const nativeElement = iconState.elementRef.nativeElement;
    this._renderer.setStyle(nativeElement, 'right', '0px');
    this._renderer.setStyle(
      nativeElement,
      'transform',
      `translateX(-${iconState.xPosition}px)`
    );
    this._renderer.setStyle(
      nativeElement,
      'opacity',
      iconState.isVisible ? 1 : 0
    );
    this._renderer.setStyle(
      nativeElement,
      'pointer-events',
      iconState.isVisible ? 'all' : 'none'
    );
  }

  private _setPreHiddenStyle(element) {}

  private _setPostHiddenStyle(element) {
    this._renderer.setStyle(element, 'pointer-events', 'none');
    this._renderer.setStyle(element, 'visibility', 'hidden');
  }

  private _setPreVisibleStyle(element, xPos) {
    this._renderer.setStyle(element, 'visibility', 'visible');
    this._renderer.setStyle(element, 'opacity', 0);
    this._renderer.setStyle(
      element,
      'transform',
      `translateX(-${xPos}px) scale(.5)`
    );
  }
  private _setPostVisibleStyle(element, xPos) {
    this._renderer.setStyle(element, 'pointer-events', 'all');
    this._renderer.setStyle(
      element,
      'transform',
      `translateX(-${xPos}px) scale(1)`
    );
    this._renderer.setStyle(element, 'opacity', 1);
    this._renderer.setStyle(element, 'visibility', 'visible');
  }

  private _setPostShiftStyle(element, xPos) {
    this._renderer.setStyle(
      element,
      'transform',
      `translateX(-${xPos}px) scale(1)`
    );
  }

  private _calculateCurrentSuffixState(): SuffixIconState[] {
    const orderedIcons = this._iconChildren
      .map((icon, index) => {
        const order = index;
        return {
          isVisible: icon.visible,
          order: order,
          xPosition: 0, // all icons will start off at the base offset
          elementRef: icon.elementRef,
        };
      })
      .sort((a, b) => b.order - a.order);
    orderedIcons
      .filter(x => x.isVisible)
      .forEach((icon, index) => {
        icon.xPosition = index * this.iconIntervalOffset + this.iconBaseOffset;
      });
    return orderedIcons;
  }
}
